import axios, { type AxiosInstance, type AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class OTMSApiService {
  private api: AxiosInstance;
  private customerApi: AxiosInstance;
  private static redirectingToLogin = false;
  private static redirectingToPortalLogin = false;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.customerApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.customerApi.interceptors.request.use((config) => {
      const token = localStorage.getItem('customer_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401 && !OTMSApiService.redirectingToLogin) {
          const path = window.location.pathname;
          if (path === '/login' || path === '/sso-callback' || path.startsWith('/portal/')) {
            return Promise.reject(error);
          }
          OTMSApiService.redirectingToLogin = true;
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    this.customerApi.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401 && !OTMSApiService.redirectingToPortalLogin) {
          const path = window.location.pathname;
          if (path === '/portal/login' || path === '/portal/register') {
            return Promise.reject(error);
          }
          OTMSApiService.redirectingToPortalLogin = true;
          localStorage.removeItem('customer_token');
          window.location.href = '/portal/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async get(path: string, params?: Record<string, any>) {
    const response = await this.api.get(path, { params });
    return response.data;
  }

  async getBlob(path: string) {
    const response = await this.api.get(path, { responseType: 'blob' });
    return response;
  }

  async post(path: string, data?: any) {
    const response = await this.api.post(path, data);
    return response.data;
  }

  async put(path: string, data?: any) {
    const response = await this.api.put(path, data);
    return response.data;
  }

  async delete(path: string) {
    const response = await this.api.delete(path);
    return response.data;
  }

  async patch(path: string, data?: any) {
    const response = await this.api.patch(path, data);
    return response.data;
  }

  async upload(path: string, formData: FormData) {
    const response = await this.api.post(path, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async healthCheck() {
    const response = await this.api.get('/health');
    return response.data;
  }

  async getBillsOfLading(params?: { page?: number; limit?: number; status?: string }) {
    const response = await this.api.get('/bl', { params });
    return response.data;
  }

  async getBillOfLading(id: string) {
    const response = await this.api.get(`/bl/${id}`);
    return response.data;
  }

  async createBillOfLading(data: any) {
    const response = await this.api.post('/bl', data);
    return response.data;
  }

  async updateBillOfLading(id: string, data: any) {
    const response = await this.api.put(`/bl/${id}`, data);
    return response.data;
  }

  async deleteBillOfLading(id: string) {
    const response = await this.api.delete(`/bl/${id}`);
    return response.data;
  }

  async getBLContainers(blId: string) {
    const response = await this.api.get(`/bl/${blId}/containers`);
    return response.data;
  }

  async getBLAmendments(blId: string) {
    const response = await this.api.get(`/bl/${blId}/amendments`);
    return response.data;
  }

  async exportBL(blId: string) {
    const response = await this.api.get(`/bl/${blId}/export`);
    return response.data;
  }

  async printBL(blId: string) {
    const response = await this.api.get(`/bl/${blId}/print`);
    return response.data;
  }

  async getContainers(params?: { page?: number; limit?: number; bl_id?: string }) {
    const response = await this.api.get('/containers', { params });
    return response.data;
  }

  async getContainer(id: string) {
    const response = await this.api.get(`/containers/${id}`);
    return response.data;
  }

  async createContainer(data: any) {
    const response = await this.api.post('/containers', data);
    return response.data;
  }

  async updateContainer(id: string, data: any) {
    const response = await this.api.put(`/containers/${id}`, data);
    return response.data;
  }

  async deleteContainer(id: string) {
    const response = await this.api.delete(`/containers/${id}`);
    return response.data;
  }

  async getContainerHistory(containerId: string) {
    const response = await this.api.get(`/containers/${containerId}/history`);
    return response.data;
  }

  async getContainerInspections(containerId: string) {
    const response = await this.api.get(`/containers/${containerId}/inspections`);
    return response.data;
  }

  async getContainerDamageReports(containerId: string) {
    const response = await this.api.get(`/containers/${containerId}/damage-reports`);
    return response.data;
  }

  async updateContainerStatus(containerId: string, status: string) {
    const response = await this.api.put(`/containers/${containerId}/status`, { status });
    return response.data;
  }

  async getInvoices(params?: { page?: number; limit?: number; status?: string }) {
    const response = await this.api.get('/invoices', { params });
    return response.data;
  }

  async getInvoice(id: string) {
    const response = await this.api.get(`/invoices/${id}`);
    return response.data;
  }

  async createInvoice(data: any) {
    const response = await this.api.post('/invoices', data);
    return response.data;
  }

  async updateInvoice(id: string, data: any) {
    const response = await this.api.put(`/invoices/${id}`, data);
    return response.data;
  }

  async deleteInvoice(id: string) {
    const response = await this.api.delete(`/invoices/${id}`);
    return response.data;
  }

  async getInvoiceLineItems(invoiceId: string) {
    const response = await this.api.get(`/invoices/${invoiceId}/items`);
    return response.data;
  }

  async getInvoicePayments(invoiceId: string) {
    const response = await this.api.get(`/invoices/${invoiceId}/payments`);
    return response.data;
  }

  async sendInvoiceEmailLegacy(invoiceId: string) {
    const response = await this.api.post(`/invoices/${invoiceId}/send-email`);
    return response.data;
  }

  async recordPayment(invoiceId: string, paymentData: any) {
    const response = await this.api.post(`/invoices/${invoiceId}/payments`, paymentData);
    return response.data;
  }

  async markInvoiceAsPaid(invoiceId: string, data?: { payment_date?: string; payment_ref?: string }) {
    const response = await this.api.patch(`/invoices/${invoiceId}/mark-paid`, data || {});
    return response.data;
  }

  async sendInvoiceReminder(invoiceId: string) {
    const response = await this.api.post(`/invoices/${invoiceId}/send-reminder`);
    return response.data;
  }

  async generateInvoicePDFLegacy(invoiceId: string) {
    const response = await this.api.get(`/invoices/${invoiceId}/generate-pdf`);
    return response.data;
  }

  async calculateDemurrage(containerId: string) {
    const response = await this.api.post('/demurrage/calculate', { container_id: containerId });
    return response.data;
  }

  async getDemurrageHistory(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    const response = await this.api.get('/demurrage/history', { params });
    return response.data;
  }

  async getDemurrageWaivers(params?: { page?: number; limit?: number; status?: string }) {
    const response = await this.api.get('/demurrage/waivers', { params });
    return response.data;
  }

  async saveDemurrageCalculation(data: any) {
    const response = await this.api.post('/demurrage/calculation', data);
    return response.data;
  }

  async generateDemurrageInvoice(calculationId: string) {
    const response = await this.api.post(`/demurrage/calculation/${calculationId}/generate-invoice`);
    return response.data;
  }

  async createWaiverRequest(data: any) {
    const response = await this.api.post('/demurrage/waivers', data);
    return response.data;
  }

  async approveWaiver(waiverId: string) {
    const response = await this.api.put(`/demurrage/waivers/${waiverId}/approve`);
    return response.data;
  }

  async rejectWaiver(waiverId: string, reason?: string) {
    const response = await this.api.put(`/demurrage/waivers/${waiverId}/reject`, { reason });
    return response.data;
  }

  async getDemurrageReports() {
    const response = await this.api.get('/demurrage/reports');
    return response.data;
  }

  async getDemurrageChartTrend() {
    const response = await this.api.get('/demurrage/charts/trend');
    return response.data;
  }

  async getDemurrageChartTopClients() {
    const response = await this.api.get('/demurrage/charts/top-clients');
    return response.data;
  }

  async getEntities(params?: { page?: number; limit?: number; type?: string }) {
    const response = await this.api.get('/entities', { params });
    return response.data;
  }

  async getEntity(id: string) {
    const response = await this.api.get(`/entities/${id}`);
    return response.data;
  }

  async createEntity(data: any) {
    const response = await this.api.post('/entities', data);
    return response.data;
  }

  async updateEntity(id: string, data: any) {
    const response = await this.api.put(`/entities/${id}`, data);
    return response.data;
  }

  async deleteEntity(id: string) {
    const response = await this.api.delete(`/entities/${id}`);
    return response.data;
  }

  async getPorts(params?: { page?: number; limit?: number; search?: string }) {
    const response = await this.api.get('/ports', { params });
    return response.data;
  }

  async getPort(id: string) {
    const response = await this.api.get(`/ports/${id}`);
    return response.data;
  }

  async createPort(data: any) {
    const response = await this.api.post('/ports', data);
    return response.data;
  }

  async updatePort(id: string, data: any) {
    const response = await this.api.put(`/ports/${id}`, data);
    return response.data;
  }

  async deletePort(id: string) {
    const response = await this.api.delete(`/ports/${id}`);
    return response.data;
  }

  async getBookings(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    const response = await this.api.get('/bookings', { params });
    return response.data;
  }

  async getBooking(id: string) {
    const response = await this.api.get(`/bookings/${id}`);
    return response.data;
  }

  async createBooking(data: any) {
    const response = await this.api.post('/bookings', data);
    return response.data;
  }

  async updateBooking(id: string, data: any) {
    const response = await this.api.put(`/bookings/${id}`, data);
    return response.data;
  }

  async deleteBooking(id: string) {
    const response = await this.api.delete(`/bookings/${id}`);
    return response.data;
  }

  async getVessels(params?: { page?: number; limit?: number; search?: string }) {
    const response = await this.api.get('/vessels', { params });
    return response.data;
  }

  async getVessel(id: string) {
    const response = await this.api.get(`/vessels/${id}`);
    return response.data;
  }

  async createVessel(data: any) {
    const response = await this.api.post('/vessels', data);
    return response.data;
  }

  async updateVessel(id: string, data: any) {
    const response = await this.api.put(`/vessels/${id}`, data);
    return response.data;
  }

  async deleteVessel(id: string) {
    const response = await this.api.delete(`/vessels/${id}`);
    return response.data;
  }

  async getVoyage(id: string) {
    const response = await this.api.get(`/voyages/${id}`);
    return response.data;
  }

  async getArrivalNotices(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    const response = await this.api.get('/arrival-notices', { params });
    return response.data;
  }

  async getArrivalNotice(id: string) {
    const response = await this.api.get(`/arrival-notices/${id}`);
    return response.data;
  }

  async createArrivalNotice(data: any) {
    const response = await this.api.post('/arrival-notices', data);
    return response.data;
  }

  async updateArrivalNotice(id: string, data: any) {
    const response = await this.api.put(`/arrival-notices/${id}`, data);
    return response.data;
  }

  async deleteArrivalNotice(id: string) {
    const response = await this.api.delete(`/arrival-notices/${id}`);
    return response.data;
  }

  async sendArrivalNotice(id: string) {
    const response = await this.api.post(`/arrival-notices/${id}/send`);
    return response.data;
  }

  async getDemurrageTariffs(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    const response = await this.api.get('/tariffs/demurrage', { params });
    return response.data;
  }

  async getDemurrageTariff(id: string) {
    const response = await this.api.get(`/tariffs/demurrage/${id}`);
    return response.data;
  }

  async createDemurrageTariff(data: any) {
    const response = await this.api.post('/tariffs/demurrage', data);
    return response.data;
  }

  async updateDemurrageTariff(id: string, data: any) {
    const response = await this.api.put(`/tariffs/demurrage/${id}`, data);
    return response.data;
  }

  async deleteDemurrageTariff(id: string) {
    const response = await this.api.delete(`/tariffs/demurrage/${id}`);
    return response.data;
  }

  async getAgentRates(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    const response = await this.api.get('/tariffs/agent-rates', { params });
    return response.data;
  }

  async getAgentRate(id: string) {
    const response = await this.api.get(`/tariffs/agent-rates/${id}`);
    return response.data;
  }

  async createAgentRate(data: any) {
    const response = await this.api.post('/tariffs/agent-rates', data);
    return response.data;
  }

  async updateAgentRate(id: string, data: any) {
    const response = await this.api.put(`/tariffs/agent-rates/${id}`, data);
    return response.data;
  }

  async deleteAgentRate(id: string) {
    const response = await this.api.delete(`/tariffs/agent-rates/${id}`);
    return response.data;
  }

  // Tasks API
  async getTasks(params?: { page?: number; limit?: number; status?: number; priority?: number; assigned_to?: number; category_id?: number }) {
    const response = await this.api.get('/tasks', { params });
    return response.data;
  }

  async getTask(id: string) {
    const response = await this.api.get(`/tasks/${id}`);
    return response.data;
  }

  async createTask(data: any) {
    const response = await this.api.post('/tasks', data);
    return response.data;
  }

  async updateTask(id: string, data: any) {
    const response = await this.api.put(`/tasks/${id}`, data);
    return response.data;
  }

  async deleteTask(id: string) {
    const response = await this.api.delete(`/tasks/${id}`);
    return response.data;
  }

  async completeTask(id: string, notes?: string) {
    const response = await this.api.put(`/tasks/${id}/complete`, { completion_notes: notes });
    return response.data;
  }

  async assignTask(id: string, userId: number) {
    const response = await this.api.put(`/tasks/${id}/assign`, { assigned_to: userId });
    return response.data;
  }

  async getTaskCategories() {
    const response = await this.api.get('/tasks/categories');
    return response.data;
  }

  async getTaskStats() {
    const response = await this.api.get('/tasks/stats');
    return response.data;
  }

  async getOverdueTasks() {
    const response = await this.api.get('/tasks/overdue');
    return response.data;
  }

  async getMyTasks() {
    const response = await this.api.get('/tasks/my-tasks');
    return response.data;
  }

  // Pipeline API (Sales Funnel)
  async getPipelines(params?: { page?: number; limit?: number; stage?: number; salesperson_id?: number }) {
    const response = await this.api.get('/pipeline', { params });
    return response.data;
  }

  async getPipeline(id: string) {
    const response = await this.api.get(`/pipeline/${id}`);
    return response.data;
  }

  async createPipeline(data: any) {
    const response = await this.api.post('/pipeline', data);
    return response.data;
  }

  async updatePipeline(id: string, data: any) {
    const response = await this.api.put(`/pipeline/${id}`, data);
    return response.data;
  }

  async deletePipeline(id: string) {
    const response = await this.api.delete(`/pipeline/${id}`);
    return response.data;
  }

  async advancePipeline(id: string, notes?: string) {
    const response = await this.api.put(`/pipeline/${id}/advance`, { notes });
    return response.data;
  }

  async winPipeline(id: string) {
    const response = await this.api.put(`/pipeline/${id}/win`);
    return response.data;
  }

  async losePipeline(id: string, reason: string) {
    const response = await this.api.put(`/pipeline/${id}/lose`, { reason });
    return response.data;
  }

  async getPipelineStats() {
    const response = await this.api.get('/pipeline/stats/summary');
    return response.data;
  }

  async getPipelineByStage() {
    const response = await this.api.get('/pipeline/by-stage');
    return response.data;
  }

  async getPipelineBoard() {
    const response = await this.api.get('/pipeline/board');
    return response.data;
  }

  async updatePipelineStage(id: number, stage: string) {
    const response = await this.api.patch(`/pipeline/${id}/stage`, { stage });
    return response.data;
  }

  // Commissions API
  async getCommissions(params?: { page?: number; limit?: number; status?: number; salesperson_id?: number }) {
    const response = await this.api.get('/commissions', { params });
    return response.data;
  }

  async getCommission(id: string) {
    const response = await this.api.get(`/commissions/${id}`);
    return response.data;
  }

  async createCommission(data: any) {
    const response = await this.api.post('/commissions', data);
    return response.data;
  }

  async updateCommission(id: string, data: any) {
    const response = await this.api.put(`/commissions/${id}`, data);
    return response.data;
  }

  async deleteCommission(id: string) {
    const response = await this.api.delete(`/commissions/${id}`);
    return response.data;
  }

  async approveCommission(id: string) {
    const response = await this.api.patch(`/commissions/${id}/approve`);
    return response.data;
  }

  async payCommission(id: string, paymentData?: any) {
    const response = await this.api.patch(`/commissions/${id}/pay`, paymentData);
    return response.data;
  }

  async getCommissionStats() {
    const response = await this.api.get('/commissions/stats/summary');
    return response.data;
  }

  async getCommissionsBySalesperson(salespersonId: string) {
    const response = await this.api.get(`/commissions/salesperson/${salespersonId}`);
    return response.data;
  }

  // Reports API
  async getShipmentReport(params?: { from?: string; to?: string; entity_id?: number; status?: number }) {
    const response = await this.api.get('/reports/shipments', { params });
    return response.data;
  }

  async getRevenueReport(params?: { from?: string; to?: string; group_by?: string }) {
    const response = await this.api.get('/reports/revenue', { params });
    return response.data;
  }

  async getAgingReport(params?: { entity_id?: number; days?: number }) {
    const response = await this.api.get('/reports/aging', { params });
    return response.data;
  }

  async getClientActivityReport(params?: { entity_id?: number; from?: string; to?: string }) {
    const response = await this.api.get('/reports/client-activity', { params });
    return response.data;
  }

  async getVesselUtilizationReport(params?: { from?: string; to?: string }) {
    const response = await this.api.get('/reports/vessel-utilization', { params });
    return response.data;
  }

  async getSalespersonPerformanceReport(params?: { from?: string; to?: string }) {
    const response = await this.api.get('/reports/performance', { params });
    return response.data;
  }

  async getReportsSummary() {
    const response = await this.api.get('/reports/summary');
    return response.data;
  }

  // Dashboard API
  async getDashboardKPIs(months?: number) {
    const response = await this.api.get('/dashboard/kpis', { params: { months } });
    return response.data;
  }

  async getDashboardFinancialSummary(months?: number) {
    const response = await this.api.get('/dashboard/financial-summary', { params: { months } });
    return response.data;
  }

  async getDashboardRecentActivity(limit?: number) {
    const response = await this.api.get('/dashboard/recent-activity', { params: { limit } });
    return response.data;
  }

  async getDashboardShipmentTrends(months?: number) {
    const response = await this.api.get('/dashboard/shipment-trends', { params: { months } });
    return response.data;
  }

  async getDashboardKPIShipments() {
    const response = await this.api.get('/dashboard/kpis/shipments');
    return response.data;
  }

  async getDashboardKPIContainers() {
    const response = await this.api.get('/dashboard/kpis/containers');
    return response.data;
  }

  async getDashboardKPIRevenue() {
    const response = await this.api.get('/dashboard/kpis/revenue');
    return response.data;
  }

  async getDashboardFinancialRevenueTrend(months?: number) {
    const response = await this.api.get('/dashboard/financial/revenue-by-month', { params: { months } });
    return response.data;
  }

  async getDashboardTopCustomers(limit?: number) {
    const response = await this.api.get('/dashboard/financial/top-customers', { params: { limit } });
    return response.data;
  }

  async getDashboardRecentBLs(limit?: number) {
    const response = await this.api.get('/dashboard/activity/recent-bls', { params: { limit } });
    return response.data;
  }

  async getDashboardRecentBookings(limit?: number) {
    const response = await this.api.get('/dashboard/activity/recent-bookings', { params: { limit } });
    return response.data;
  }

  async getDashboardRecentInvoices(limit?: number) {
    const response = await this.api.get('/dashboard/activity/recent-invoices', { params: { limit } });
    return response.data;
  }

  async getDashboardUpcomingArrivals(days?: number) {
    const response = await this.api.get('/dashboard/activity/upcoming-arrivals', { params: { days } });
    return response.data;
  }

  async getDashboardAlerts() {
    const response = await this.api.get('/dashboard/alerts');
    return response.data;
  }

  async getDashboardContainerStatus() {
    const response = await this.api.get('/dashboard/container-status');
    return response.data;
  }

  // Control Tower APIs
  async getControlTowerSummary() {
    const response = await this.api.get('/dashboard/control-tower');
    return response.data;
  }

  async getControlTowerShipments(limit?: number) {
    const response = await this.api.get('/dashboard/control-tower/shipments', { params: { limit } });
    return response.data;
  }

  async getControlTowerExceptions(limit?: number) {
    const response = await this.api.get('/dashboard/control-tower/exceptions', { params: { limit } });
    return response.data;
  }

  async getControlTowerSLATimers() {
    const response = await this.api.get('/dashboard/control-tower/sla-timers');
    return response.data;
  }

  async getControlTowerDwellTime() {
    const response = await this.api.get('/dashboard/control-tower/dwell-time');
    return response.data;
  }

  // Profitability Analytics APIs
  async getProfitabilityMargins(params?: { groupBy?: string; startDate?: string; endDate?: string }) {
    const response = await this.api.get('/analytics/profitability/margins', { params });
    return response.data;
  }

  async getQuoteConversion() {
    const response = await this.api.get('/analytics/profitability/quote-conversion');
    return response.data;
  }

  async getRevenueAging() {
    const response = await this.api.get('/analytics/profitability/aging');
    return response.data;
  }

  async getCustomer360(customerId: string) {
    const response = await this.api.get(`/analytics/customers/${customerId}/360`);
    return response.data;
  }

  async searchBillsOfLading(query: string) {
    const response = await this.api.get(`/bl/search/${encodeURIComponent(query)}`);
    return response.data;
  }

  async searchContainers(query: string) {
    const response = await this.api.get(`/containers/search/${encodeURIComponent(query)}`);
    return response.data;
  }

  async searchEntities(query: string) {
    const response = await this.api.get(`/entities/search/${encodeURIComponent(query)}`);
    return response.data;
  }

  async searchPorts(query: string) {
    const response = await this.api.get(`/ports/search/${encodeURIComponent(query)}`);
    return response.data;
  }

  async searchTasks(query: string) {
    const response = await this.api.get(`/tasks/search/${encodeURIComponent(query)}`);
    return response.data;
  }

  async globalSearch(query: string) {
    const [bls, containers, entities, ports, tasks] = await Promise.allSettled([
      this.searchBillsOfLading(query),
      this.searchContainers(query),
      this.searchEntities(query),
      this.searchPorts(query),
      this.searchTasks(query),
    ]);
    return {
      success: true,
      data: {
        bills_of_lading: bls.status === 'fulfilled' ? bls.value.data : [],
        containers: containers.status === 'fulfilled' ? containers.value.data : [],
        entities: entities.status === 'fulfilled' ? entities.value.data : [],
        ports: ports.status === 'fulfilled' ? ports.value.data : [],
        tasks: tasks.status === 'fulfilled' ? tasks.value.data : [],
      }
    };
  }

  async cancelInvoice(invoiceId: string, reason?: string) {
    const response = await this.api.patch(`/invoices/${invoiceId}/cancel`, { reason });
    return response.data;
  }

  async addInvoiceLineItem(invoiceId: string, data: any) {
    const response = await this.api.post(`/invoices/${invoiceId}/items`, data);
    return response.data;
  }

  async updateInvoiceLineItem(invoiceId: string, itemId: string, data: any) {
    const response = await this.api.put(`/invoices/${invoiceId}/items/${itemId}`, data);
    return response.data;
  }

  async deleteInvoiceLineItem(invoiceId: string, itemId: string) {
    const response = await this.api.delete(`/invoices/${invoiceId}/items/${itemId}`);
    return response.data;
  }

  async getInvoicesByStatus(status: string) {
    const response = await this.api.get(`/invoices/status/${status}`);
    return response.data;
  }

  async getInvoiceSummaryByClient() {
    const response = await this.api.get('/invoices/summary/by-client');
    return response.data;
  }

  async getPendingTasks() {
    const response = await this.api.get('/tasks/pending');
    return response.data;
  }

  async getTasksByCategory(categoryId: number) {
    const response = await this.api.get(`/tasks/category/${categoryId}`);
    return response.data;
  }

  async getTasksByAssignee(userId: number) {
    const response = await this.api.get(`/tasks/assignee/${userId}`);
    return response.data;
  }

  async getTasksByEntity(entityId: number) {
    const response = await this.api.get(`/tasks/entity/${entityId}`);
    return response.data;
  }

  async getTasksInDateRange(from: string, to: string) {
    const response = await this.api.get('/tasks/date-range', { params: { from, to } });
    return response.data;
  }

  async getIdleContainers() {
    const response = await this.api.get('/containers/status/idle');
    return response.data;
  }

  async linkContainerToBL(containerId: string, blId: string) {
    const response = await this.api.post(`/containers/${containerId}/link-bl`, { bl_id: blId });
    return response.data;
  }

  async getPipelineByStageId(stage: number) {
    const response = await this.api.get(`/pipeline/stage/${stage}`);
    return response.data;
  }


  async getPendingCommissions() {
    const response = await this.api.get('/commissions/pending');
    return response.data;
  }

  async getDemurrageActiveCases() {
    const response = await this.api.get('/demurrage/active-cases');
    return response.data;
  }

  async getDemurrageForecast() {
    const response = await this.api.get('/demurrage/forecast');
    return response.data;
  }

  async getDemurrageContainerHistory(containerId: string) {
    const response = await this.api.get(`/demurrage/container/${containerId}/history`);
    return response.data;
  }

  async getDemurrageStatsSummary() {
    const response = await this.api.get('/demurrage/stats/summary');
    return response.data;
  }

  // Quotations
  async getQuotations(params?: { page?: number; limit?: number; status?: string; customer_id?: string }) {
    const response = await this.api.get('/quotes', { params });
    return response.data;
  }

  async getQuotation(id: string) {
    const response = await this.api.get(`/quotes/${id}`);
    return response.data;
  }

  async createQuotation(data: any) {
    const response = await this.api.post('/quotes', data);
    return response.data;
  }

  async updateQuotation(id: string, data: any) {
    const response = await this.api.put(`/quotes/${id}`, data);
    return response.data;
  }

  async deleteQuotation(id: string) {
    const response = await this.api.delete(`/quotes/${id}`);
    return response.data;
  }

  async sendQuotation(id: string) {
    const response = await this.api.post(`/quotes/${id}/send`);
    return response.data;
  }

  async convertQuoteToBooking(id: string) {
    const response = await this.api.post(`/quotes/${id}/convert`);
    return response.data;
  }

  async getQuotationVersions(id: string) {
    const response = await this.api.get(`/quotes/${id}/versions`);
    return response.data;
  }

  // Rate Cards
  async getRateCards(params?: { page?: number; limit?: number; origin?: string; destination?: string; carrier?: string; container_type?: string; valid_only?: boolean }) {
    const response = await this.api.get('/rate-cards', { params });
    return response.data;
  }

  async getRateCard(id: string) {
    const response = await this.api.get(`/rate-cards/${id}`);
    return response.data;
  }

  async createRateCard(data: any) {
    const response = await this.api.post('/rate-cards', data);
    return response.data;
  }

  async updateRateCard(id: string, data: any) {
    const response = await this.api.put(`/rate-cards/${id}`, data);
    return response.data;
  }

  async deleteRateCard(id: string) {
    const response = await this.api.delete(`/rate-cards/${id}`);
    return response.data;
  }

  async searchRates(params: { origin: string; destination: string; container_type?: string; date?: string }) {
    const response = await this.api.get('/rate-cards/search', { params });
    return response.data;
  }

  // Document Generation
  async generateQuotePDF(id: string): Promise<Blob> {
    const response = await this.api.post(`/documents/quotes/${id}/generate-pdf`, {}, {
      responseType: 'blob'
    });
    return response.data;
  }

  async sendQuoteEmail(id: string, data: { to: string[]; cc?: string[]; subject?: string; message?: string }) {
    const response = await this.api.post(`/documents/quotes/${id}/send-email`, data);
    return response.data;
  }

  async generateInvoicePDF(id: string): Promise<Blob> {
    const response = await this.api.post(`/documents/invoices/${id}/generate-pdf`, {}, {
      responseType: 'blob'
    });
    return response.data;
  }

  async sendInvoiceEmail(id: string, data: { to: string[]; cc?: string[]; subject?: string; message?: string }) {
    const response = await this.api.post(`/documents/invoices/${id}/send-email`, data);
    return response.data;
  }

  async generateBLPDF(id: string): Promise<Blob> {
    const response = await this.api.post(`/documents/bl/${id}/generate-pdf`, {}, {
      responseType: 'blob'
    });
    return response.data;
  }

  async sendBLEmail(id: string, data: { to: string[]; cc?: string[]; subject?: string; message?: string }) {
    const response = await this.api.post(`/documents/bl/${id}/send-email`, data);
    return response.data;
  }

  async getDocumentTemplates(type?: string) {
    const response = await this.api.get('/documents/templates', { params: { type } });
    return response.data;
  }

  async getDocumentAudit(docType?: string, recordId?: string) {
    const response = await this.api.get('/documents/audit', { params: { doc_type: docType, record_id: recordId } });
    return response.data;
  }

  // Settings - User Profile
  async getSettingsProfile() {
    const response = await this.api.get('/settings/profile');
    return response.data;
  }

  async updateSettingsProfile(data: any) {
    const response = await this.api.put('/settings/profile', data);
    return response.data;
  }

  // Settings - Company Profile
  async getSettingsCompany() {
    const response = await this.api.get('/settings/company');
    return response.data;
  }

  async updateSettingsCompany(data: any) {
    const response = await this.api.put('/settings/company', data);
    return response.data;
  }

  // Settings - System Settings
  async getSettingsSystem() {
    const response = await this.api.get('/settings/system');
    return response.data;
  }

  async updateSettingsSystem(data: Record<string, any>) {
    const response = await this.api.put('/settings/system', data);
    return response.data;
  }

  async getSystemSetting(key: string) {
    const response = await this.api.get(`/settings/system/${key}`);
    return response.data;
  }

  async updateSystemSetting(key: string, value: any) {
    const response = await this.api.put(`/settings/system/${key}`, { value });
    return response.data;
  }

  // Settings - Notification Preferences
  async getSettingsNotifications() {
    const response = await this.api.get('/settings/notifications');
    return response.data;
  }

  async updateSettingsNotifications(data: { notification_type: string; email_enabled: boolean; push_enabled: boolean; in_app_enabled: boolean }) {
    const response = await this.api.put('/settings/notifications', data);
    return response.data;
  }

  // Settings - Password Change
  async changePassword(currentPassword: string, newPassword: string) {
    const response = await this.api.post('/settings/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  }

  // Entity Contacts
  async getEntityContacts(entityId: string) {
    const response = await this.api.get(`/entities/${entityId}/contacts`);
    return response.data;
  }

  async createEntityContact(entityId: string, data: any) {
    const response = await this.api.post(`/entities/${entityId}/contacts`, data);
    return response.data;
  }

  async updateEntityContact(entityId: string, contactId: string, data: any) {
    const response = await this.api.put(`/entities/${entityId}/contacts/${contactId}`, data);
    return response.data;
  }

  async deleteEntityContact(entityId: string, contactId: string) {
    const response = await this.api.delete(`/entities/${entityId}/contacts/${contactId}`);
    return response.data;
  }

  // Entity Addresses
  async getEntityAddresses(entityId: string) {
    const response = await this.api.get(`/entities/${entityId}/addresses`);
    return response.data;
  }

  async createEntityAddress(entityId: string, data: any) {
    const response = await this.api.post(`/entities/${entityId}/addresses`, data);
    return response.data;
  }

  async updateEntityAddress(entityId: string, addressId: string, data: any) {
    const response = await this.api.put(`/entities/${entityId}/addresses/${addressId}`, data);
    return response.data;
  }

  async deleteEntityAddress(entityId: string, addressId: string) {
    const response = await this.api.delete(`/entities/${entityId}/addresses/${addressId}`);
    return response.data;
  }

  // Entity Credit
  async getEntityCredit(entityId: string) {
    const response = await this.api.get(`/entities/${entityId}/credit`);
    return response.data;
  }

  async updateEntityCredit(entityId: string, data: any) {
    const response = await this.api.put(`/entities/${entityId}/credit`, data);
    return response.data;
  }

  // Active Payment Terms (for dropdowns)
  async getActivePaymentTerms() {
    const response = await this.api.get('/payment-terms/active');
    return response.data;
  }

  // Import API
  async downloadImportTemplate(type: string) {
    const response = await this.api.get(`/import/templates/${type}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async uploadImportFile(type: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const response = await this.api.post('/import/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getImportJobs(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/import/jobs', { params });
    return response.data;
  }

  async getImportJobDetails(jobId: string) {
    const response = await this.api.get(`/import/jobs/${jobId}`);
    return response.data;
  }

  async processImportJob(jobId: string, skipErrors: boolean = false) {
    const response = await this.api.post(`/import/jobs/${jobId}/process`, { skip_errors: skipErrors });
    return response.data;
  }

  // Trade Lanes API
  async getTradeLanes(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/trade-lanes', { params });
    return response.data;
  }

  async getTradeLane(id: string) {
    const response = await this.api.get(`/trade-lanes/${id}`);
    return response.data;
  }

  async searchTradeLanes(query: string) {
    const response = await this.api.get(`/trade-lanes/search/${encodeURIComponent(query)}`);
    return response.data;
  }

  async getActiveTradeLanes() {
    const response = await this.api.get('/trade-lanes/active');
    return response.data;
  }

  async createTradeLane(data: any) {
    const response = await this.api.post('/trade-lanes', data);
    return response.data;
  }

  async updateTradeLane(id: string, data: any) {
    const response = await this.api.put(`/trade-lanes/${id}`, data);
    return response.data;
  }

  async deleteTradeLane(id: string) {
    const response = await this.api.delete(`/trade-lanes/${id}`);
    return response.data;
  }

  async getTradeLanesByPorts(originPortId?: string, destinationPortId?: string) {
    const response = await this.api.get('/trade-lanes/by-ports', {
      params: { origin_port_id: originPortId, destination_port_id: destinationPortId }
    });
    return response.data;
  }

  // =====================================================
  // Job Costing API
  // =====================================================
  async getJobs(params?: { page?: number; limit?: number; search?: string; status?: string; client_id?: string; job_type?: string; role?: string; from_date?: string; to_date?: string }) {
    const response = await this.api.get('/jobs', { params });
    return response.data;
  }

  async getJob(id: string) {
    const response = await this.api.get(`/jobs/${id}`);
    return response.data;
  }

  async getJobsSummary() {
    const response = await this.api.get('/jobs/summary');
    return response.data;
  }

  async getJobsProfitability(params?: { from_date?: string; to_date?: string; client_id?: string; group_by?: string }) {
    const response = await this.api.get('/jobs/profitability', { params });
    return response.data;
  }

  async getChargeCodes(params?: { category?: string; charge_type?: string }) {
    const response = await this.api.get('/jobs/charge-codes', { params });
    return response.data;
  }

  async getEcosystemServices(params?: { category?: string; search?: string }) {
    const response = await this.api.get('/jobs/ecosystem-services', { params });
    return response.data;
  }

  async createJob(data: any) {
    const response = await this.api.post('/jobs', data);
    return response.data;
  }

  async updateJob(id: string, data: any) {
    const response = await this.api.put(`/jobs/${id}`, data);
    return response.data;
  }

  async closeJob(id: string) {
    const response = await this.api.post(`/jobs/${id}/close`);
    return response.data;
  }

  async updateJobStatus(id: string, status: string, cancel_reason?: string) {
    const response = await this.api.put(`/jobs/${id}/status`, { status, cancel_reason });
    return response.data;
  }

  async getServiceRequestsByJob(jobId: string | number) {
    const response = await this.api.get(`/service-requests/by-job/${jobId}`);
    return response.data;
  }

  async getServiceRequests(params?: { page?: number; limit?: number; status?: string; category_id?: string; my?: string }) {
    const response = await this.api.get('/service-requests', { params });
    return response.data;
  }

  async updateServiceRequestStatus(id: string | number, status: string) {
    const response = await this.api.patch(`/service-requests/${id}/status`, { status });
    return response.data;
  }

  async updateServiceRequest(id: string | number, data: any) {
    const response = await this.api.put(`/service-requests/${id}`, data);
    return response.data;
  }

  async getSRQuotations(params?: { page?: number; limit?: number; status?: string; provider_job_id?: string | number; linked_job_id?: string | number }) {
    const response = await this.api.get('/quotations', { params });
    return response.data;
  }

  async getSRQuotationsBySR(srId: string | number) {
    const response = await this.api.get(`/quotations/by-sr/${srId}`);
    return response.data;
  }

  async getSRQuotation(id: string | number) {
    const response = await this.api.get(`/quotations/${id}`);
    return response.data;
  }

  async createSRQuotation(data: any) {
    const response = await this.api.post('/quotations', data);
    return response.data;
  }

  async updateQuotationStatus(id: string | number, status: string) {
    const response = await this.api.patch(`/quotations/${id}/status`, { status });
    return response.data;
  }

  async getJobMembers(jobId: string | number) {
    const response = await this.api.get(`/job-members/${jobId}`);
    return response.data;
  }

  async addJobMember(jobId: string | number, data: { job_number: string; user_email: string; user_name?: string; member_role?: string }) {
    const response = await this.api.post(`/job-members/${jobId}`, data);
    return response.data;
  }

  async removeJobMember(jobId: string | number, memberId: string | number) {
    const response = await this.api.delete(`/job-members/${jobId}/${memberId}`);
    return response.data;
  }

  async deleteJob(id: string) {
    const response = await this.api.delete(`/jobs/${id}`);
    return response.data;
  }

  async getJobTeam(jobId: string) {
    const response = await this.api.get(`/jobs/${jobId}/team`);
    return response.data;
  }

  async addJobTeamMember(jobId: string, userId: number, role?: string) {
    const response = await this.api.post(`/jobs/${jobId}/team`, { user_id: userId, role });
    return response.data;
  }

  async removeJobTeamMember(jobId: string, userId: number) {
    const response = await this.api.delete(`/jobs/${jobId}/team/${userId}`);
    return response.data;
  }

  async getAvailableUsersForJob(jobId: string, search?: string) {
    const response = await this.api.get(`/jobs/${jobId}/available-users`, { params: { search } });
    return response.data;
  }

  // Job Cost Lines
  async getJobFinancials(jobId: string) {
    const response = await this.api.get(`/jobs/${jobId}/financials`);
    return response.data;
  }

  async getJobCosts(jobId: string) {
    const response = await this.api.get(`/jobs/${jobId}/costs`);
    return response.data;
  }

  async createJobCost(jobId: string, data: any) {
    const response = await this.api.post(`/jobs/${jobId}/costs`, data);
    return response.data;
  }

  async updateJobCost(jobId: string, costId: string, data: any) {
    const response = await this.api.put(`/jobs/${jobId}/costs/${costId}`, data);
    return response.data;
  }

  async deleteJobCost(jobId: string, costId: string) {
    const response = await this.api.delete(`/jobs/${jobId}/costs/${costId}`);
    return response.data;
  }

  // Job Revenue Lines
  async getJobRevenues(jobId: string) {
    const response = await this.api.get(`/jobs/${jobId}/revenues`);
    return response.data;
  }

  async createJobRevenue(jobId: string, data: any) {
    const response = await this.api.post(`/jobs/${jobId}/revenues`, data);
    return response.data;
  }

  async updateJobRevenue(jobId: string, revenueId: string, data: any) {
    const response = await this.api.put(`/jobs/${jobId}/revenues/${revenueId}`, data);
    return response.data;
  }

  async deleteJobRevenue(jobId: string, revenueId: string) {
    const response = await this.api.delete(`/jobs/${jobId}/revenues/${revenueId}`);
    return response.data;
  }

  // =====================================================
  // Shipment Tracking API
  // =====================================================
  async getShipments(params?: { page?: number; limit?: number; search?: string; status?: string; client_id?: string; transport_mode?: string; exception_only?: string; service_type?: string; from_date?: string; to_date?: string }) {
    const response = await this.api.get('/shipments', { params });
    return response.data;
  }

  async getShipment(id: string) {
    const response = await this.api.get(`/shipments/${id}`);
    return response.data;
  }

  async getShipmentsSummary() {
    const response = await this.api.get('/shipments/summary');
    return response.data;
  }

  async getShipmentExceptions(limit?: number) {
    const response = await this.api.get('/shipments/exceptions', { params: { limit } });
    return response.data;
  }

  async getMilestoneTemplates(transportMode?: string) {
    const response = await this.api.get('/shipments/milestones/templates', { params: { transport_mode: transportMode } });
    return response.data;
  }

  async getShipmentTimeline(id: string) {
    const response = await this.api.get(`/shipments/${id}/timeline`);
    return response.data;
  }

  async getShipmentContainers(id: string) {
    const response = await this.api.get(`/shipments/${id}/containers`);
    return response.data;
  }

  async getShipmentEvents(id: string) {
    const response = await this.api.get(`/shipments/${id}/events`);
    return response.data;
  }

  async createShipment(data: any) {
    const response = await this.api.post('/shipments', data);
    return response.data;
  }

  async updateShipment(id: string, data: any) {
    const response = await this.api.put(`/shipments/${id}`, data);
    return response.data;
  }

  async deleteShipment(id: string) {
    const response = await this.api.delete(`/shipments/${id}`);
    return response.data;
  }

  async addShipmentContainer(shipmentId: string, data: any) {
    const response = await this.api.post(`/shipments/${shipmentId}/containers`, data);
    return response.data;
  }

  async removeShipmentContainer(shipmentId: string, containerId: string) {
    const response = await this.api.delete(`/shipments/${shipmentId}/containers/${containerId}`);
    return response.data;
  }

  async addShipmentEvent(shipmentId: string, data: any) {
    const response = await this.api.post(`/shipments/${shipmentId}/events`, data);
    return response.data;
  }

  async updateShipmentEvent(shipmentId: string, eventId: string, data: any) {
    const response = await this.api.put(`/shipments/${shipmentId}/events/${eventId}`, data);
    return response.data;
  }

  async deleteShipmentEvent(shipmentId: string, eventId: string) {
    const response = await this.api.delete(`/shipments/${shipmentId}/events/${eventId}`);
    return response.data;
  }

  // =====================================================
  // Notifications API
  // =====================================================
  async getNotificationTemplates(params?: { page?: number; limit?: number; category?: string; channel?: string; active?: boolean }) {
    const response = await this.api.get('/notifications/templates', { params });
    return response.data;
  }

  async getNotificationTemplate(id: number | string) {
    const response = await this.api.get(`/notifications/templates/${id}`);
    return response.data;
  }

  async createNotificationTemplate(data: any) {
    const response = await this.api.post('/notifications/templates', data);
    return response.data;
  }

  async updateNotificationTemplate(id: number | string, data: any) {
    const response = await this.api.put(`/notifications/templates/${id}`, data);
    return response.data;
  }

  async deleteNotificationTemplate(id: number | string) {
    const response = await this.api.delete(`/notifications/templates/${id}`);
    return response.data;
  }

  async getNotificationRules(params?: { page?: number; limit?: number; event_type?: string; trigger_scope?: string }) {
    const response = await this.api.get('/notifications/rules', { params });
    return response.data;
  }

  async getNotificationRule(id: number | string) {
    const response = await this.api.get(`/notifications/rules/${id}`);
    return response.data;
  }

  async createNotificationRule(data: any) {
    const response = await this.api.post('/notifications/rules', data);
    return response.data;
  }

  async updateNotificationRule(id: number | string, data: any) {
    const response = await this.api.put(`/notifications/rules/${id}`, data);
    return response.data;
  }

  async deleteNotificationRule(id: number | string) {
    const response = await this.api.delete(`/notifications/rules/${id}`);
    return response.data;
  }

  async getNotificationAudit(params?: { page?: number; limit?: number; target_type?: string; target_id?: string; status?: string }) {
    const response = await this.api.get('/notifications/audit', { params });
    return response.data;
  }

  async getNotificationAuditSummary() {
    const response = await this.api.get('/notifications/audit/summary');
    return response.data;
  }

  async previewNotification(templateId: number | string, variables: Record<string, string>) {
    const response = await this.api.post('/notifications/preview', { template_id: templateId, variables });
    return response.data;
  }

  async sendNotification(data: { template_id: string; target_type?: string; target_id?: number; recipient_email: string; variables?: Record<string, string> }) {
    const response = await this.api.post('/notifications/send', data);
    return response.data;
  }

  async resendNotification(auditId: string) {
    const response = await this.api.post(`/notifications/audit/${auditId}/resend`);
    return response.data;
  }

  // =====================================================
  // Booking Rates API
  // =====================================================

  async getBookingRates(bookingId: string) {
    const response = await this.api.get(`/bookings/${bookingId}/rates`);
    return response.data;
  }

  async createBookingRate(bookingId: string, data: any) {
    const response = await this.api.post(`/bookings/${bookingId}/rates`, data);
    return response.data;
  }

  async updateBookingRate(bookingId: string, rateId: string, data: any) {
    const response = await this.api.put(`/bookings/${bookingId}/rates/${rateId}`, data);
    return response.data;
  }

  async deleteBookingRate(bookingId: string, rateId: string) {
    const response = await this.api.delete(`/bookings/${bookingId}/rates/${rateId}`);
    return response.data;
  }

  async getBookingMargin(bookingId: string) {
    const response = await this.api.get(`/bookings/${bookingId}/margin`);
    return response.data;
  }

  async generatePreInvoice(bookingId: string) {
    const response = await this.api.post(`/bookings/${bookingId}/generate-pre-invoice`);
    return response.data;
  }

  async getBookingPreInvoices(bookingId: string) {
    const response = await this.api.get(`/bookings/${bookingId}/pre-invoices`);
    return response.data;
  }

  async createBookingShipment(bookingId: string) {
    const response = await this.api.post(`/bookings/${bookingId}/create-shipment`);
    return response.data;
  }

  async getBookingShipment(bookingId: string) {
    const response = await this.api.get(`/bookings/${bookingId}/shipment`);
    return response.data;
  }

  async createBookingBL(bookingId: string) {
    const response = await this.api.post(`/bookings/${bookingId}/create-bl`);
    return response.data;
  }

  async getBookingBL(bookingId: string) {
    const response = await this.api.get(`/bookings/${bookingId}/bl`);
    return response.data;
  }

  async getBookingWorkflowStatus(bookingId: string) {
    const response = await this.api.get(`/bookings/${bookingId}/workflow-status`);
    return response.data;
  }

  // =====================================================
  // Freight Charges API
  // =====================================================

  async getFreightCharges(params?: { page?: number; limit?: number; search?: string }) {
    const response = await this.api.get('/freight-charges', { params });
    return response.data;
  }

  async getFreightCharge(id: string) {
    const response = await this.api.get(`/freight-charges/${id}`);
    return response.data;
  }

  async createFreightCharge(data: any) {
    const response = await this.api.post('/freight-charges', data);
    return response.data;
  }

  async updateFreightCharge(id: string, data: any) {
    const response = await this.api.put(`/freight-charges/${id}`, data);
    return response.data;
  }

  async deleteFreightCharge(id: string) {
    const response = await this.api.delete(`/freight-charges/${id}`);
    return response.data;
  }

  async getFreightChargeLines(chargeId: string) {
    const response = await this.api.get(`/freight-charges/${chargeId}/lines`);
    return response.data;
  }

  async createFreightChargeLine(chargeId: string, data: any) {
    const response = await this.api.post(`/freight-charges/${chargeId}/lines`, data);
    return response.data;
  }

  async updateFreightChargeLine(chargeId: string, lineId: string, data: any) {
    const response = await this.api.put(`/freight-charges/${chargeId}/lines/${lineId}`, data);
    return response.data;
  }

  async deleteFreightChargeLine(chargeId: string, lineId: string) {
    const response = await this.api.delete(`/freight-charges/${chargeId}/lines/${lineId}`);
    return response.data;
  }

  async getFreightChargesForBL(blId: string) {
    const response = await this.api.get(`/freight-charges/bl/${blId}`);
    return response.data;
  }

  // =====================================================
  // Calculator API
  // =====================================================

  async calculateDemurrageFromTariff(data: { duration: number; tariff_code: number; container_size: number }) {
    const response = await this.api.post('/calculator/demurrage', data);
    return response.data;
  }

  async convertCurrency(data: { amount: number; from_currency_code: string | number; invoice_id?: number }) {
    const response = await this.api.post('/calculator/currency', data);
    return response.data;
  }

  async calculateWeightShare(data: { bl_id: number; container_id: number }) {
    const response = await this.api.post('/calculator/weight-share', data);
    return response.data;
  }

  async convertDate(data: { persian_date?: string; gregorian_date?: string }) {
    const response = await this.api.post('/calculator/dates', data);
    return response.data;
  }

  // =====================================================
  // Demurrage Tariffs API (new /demurrage-tariffs endpoint)
  // =====================================================

  async getDemurrageTariffCodes(params?: { page?: number; limit?: number; search?: string }) {
    const response = await this.api.get('/demurrage-tariffs', { params });
    return response.data;
  }

  async getDemurrageTariffByCode(code: string) {
    const response = await this.api.get(`/demurrage-tariffs/${code}`);
    return response.data;
  }

  async createDemurrageTariffCode(data: any) {
    const response = await this.api.post('/demurrage-tariffs', data);
    return response.data;
  }

  async updateDemurrageTariffByCode(code: string, data: any) {
    const response = await this.api.put(`/demurrage-tariffs/${code}`, data);
    return response.data;
  }

  async deleteDemurrageTariffByCode(code: string) {
    const response = await this.api.delete(`/demurrage-tariffs/${code}`);
    return response.data;
  }

  async getServices(params?: { page?: number; limit?: number; search?: string }) {
    const response = await this.api.get('/services', { params });
    return response.data;
  }

  // =====================================================
  // Booking Box Details API
  // =====================================================

  async getBookingBoxes(bookingId: string) {
    const response = await this.api.get(`/bookings/${bookingId}/boxes`);
    return response.data;
  }

  async createBookingBox(bookingId: string, data: any) {
    const response = await this.api.post(`/bookings/${bookingId}/boxes`, data);
    return response.data;
  }

  async updateBookingBox(bookingId: string, boxId: string, data: any) {
    const response = await this.api.put(`/bookings/${bookingId}/boxes/${boxId}`, data);
    return response.data;
  }

  async deleteBookingBox(bookingId: string, boxId: string) {
    const response = await this.api.delete(`/bookings/${bookingId}/boxes/${boxId}`);
    return response.data;
  }

  // =====================================================
  // Booking Dimensions API
  // =====================================================

  async getBookingDimensions(bookingId: string) {
    const response = await this.api.get(`/bookings/${bookingId}/dimensions`);
    return response.data;
  }

  async createBookingDimension(bookingId: string, data: any) {
    const response = await this.api.post(`/bookings/${bookingId}/dimensions`, data);
    return response.data;
  }

  async updateBookingDimension(bookingId: string, dimId: string, data: any) {
    const response = await this.api.put(`/bookings/${bookingId}/dimensions/${dimId}`, data);
    return response.data;
  }

  async deleteBookingDimension(bookingId: string, dimId: string) {
    const response = await this.api.delete(`/bookings/${bookingId}/dimensions/${dimId}`);
    return response.data;
  }

  // =====================================================
  // Customer Authentication API
  // =====================================================

  async customerLogin(email: string, password: string) {
    const response = await this.api.post('/customer-auth/login', { email, password });
    return response.data;
  }

  async customerRegister(data: { email: string; password: string; first_name?: string; last_name?: string; phone?: string; entity_id?: number }) {
    const response = await this.api.post('/customer-auth/register', data);
    return response.data;
  }

  async customerForgotPassword(email: string) {
    const response = await this.api.post('/customer-auth/forgot-password', { email });
    return response.data;
  }

  async customerResetPassword(token: string, password: string) {
    const response = await this.api.post('/customer-auth/reset-password', { token, password });
    return response.data;
  }

  async getCustomerProfile() {
    const response = await this.customerApi.get('/customer-auth/me');
    return response.data;
  }

  async updateCustomerProfile(data: { first_name?: string; last_name?: string; phone?: string; current_password?: string; new_password?: string }) {
    const response = await this.customerApi.put('/customer-auth/me', data);
    return response.data;
  }

  // =====================================================
  // Customer Portal API (uses customerApi with customer_token)
  // =====================================================

  async getPortalDashboard(entityId: number) {
    const response = await this.customerApi.get('/portal/dashboard', { params: { entity_id: entityId } });
    return response.data;
  }

  async getPortalShipments(entityId: number, params?: { page?: number; limit?: number; status?: string; search?: string }) {
    const response = await this.customerApi.get('/portal/shipments', { params: { entity_id: entityId, ...params } });
    return response.data;
  }

  async getPortalShipmentDetails(entityId: number, id: number) {
    const response = await this.customerApi.get(`/portal/shipments/${id}`, { params: { entity_id: entityId } });
    return response.data;
  }

  async getPortalInvoices(entityId: number, params?: { page?: number; limit?: number; status?: string; search?: string }) {
    const response = await this.customerApi.get('/portal/invoices', { params: { entity_id: entityId, ...params } });
    return response.data;
  }

  async getPortalQuotes(entityId: number, params?: { page?: number; limit?: number; status?: string; search?: string }) {
    const response = await this.customerApi.get('/portal/quotes', { params: { entity_id: entityId, ...params } });
    return response.data;
  }

  // =====================================================
  // Credit Control API
  // =====================================================

  async getCreditControlOverview() {
    const response = await this.api.get('/credit-control/overview');
    return response.data;
  }

  async getCreditLimits(params?: { page?: number; limit?: number; status?: string }) {
    const response = await this.api.get('/credit-control/limits', { params });
    return response.data;
  }

  async getCreditLimit(entityId: string) {
    const response = await this.api.get(`/credit-control/limits/${entityId}`);
    return response.data;
  }

  async createCreditLimit(data: any) {
    const response = await this.api.post('/credit-control/limits', data);
    return response.data;
  }

  async updateCreditLimit(entityId: string, data: any) {
    const response = await this.api.put(`/credit-control/limits/${entityId}`, data);
    return response.data;
  }

  async getCreditAgingReport() {
    const response = await this.api.get('/credit-control/aging');
    return response.data;
  }

  async getEntityAging(entityId: string) {
    const response = await this.api.get(`/credit-control/aging/${entityId}`);
    return response.data;
  }

  async getCreditPaymentHistory(params?: { page?: number; limit?: number; entityId?: string }) {
    const response = await this.api.get('/credit-control/payments', { params });
    return response.data;
  }

  async recordCreditPayment(data: any) {
    const response = await this.api.post('/credit-control/payments', data);
    return response.data;
  }

  async getCustomersAtRisk() {
    const response = await this.api.get('/credit-control/at-risk');
    return response.data;
  }

  // =====================================================
  // Analytics API
  // =====================================================

  async getFinancialPnL(params?: { groupBy?: string; startDate?: string; endDate?: string; customerId?: string; page?: number; limit?: number }) {
    const response = await this.api.get('/analytics/financial/pnl', { params });
    return response.data;
  }

  async exportFinancialPnL(params: { format: 'xlsx' | 'pdf'; groupBy?: string; startDate?: string; endDate?: string; customerId?: string }) {
    const response = await this.api.get('/analytics/financial/pnl/export', { 
      params,
      responseType: 'blob'
    });
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pnl_report_${params.groupBy || 'job'}_${Date.now()}.${params.format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async getOperationalKPIs(params?: { startDate?: string; endDate?: string }) {
    const response = await this.api.get('/analytics/operational/kpis', { params });
    return response.data;
  }

  async exportOperationalKPIs(params: { format: 'xlsx' | 'pdf' }) {
    const response = await this.api.get('/analytics/operational/kpis/export', { 
      params,
      responseType: 'blob'
    });
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `operational_kpis_${Date.now()}.${params.format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async getAnalyticsSummary() {
    const response = await this.api.get('/analytics/summary');
    return response.data;
  }

  // =====================================================
  // Automation API
  // =====================================================

  async getAutomationRules(params?: { page?: number; limit?: number; rule_type?: string; is_active?: string }) {
    const response = await this.api.get('/automation/rules', { params });
    return response.data;
  }

  async getAutomationRule(id: string) {
    const response = await this.api.get(`/automation/rules/${id}`);
    return response.data;
  }

  async createAutomationRule(data: any) {
    const response = await this.api.post('/automation/rules', data);
    return response.data;
  }

  async updateAutomationRule(id: string, data: any) {
    const response = await this.api.put(`/automation/rules/${id}`, data);
    return response.data;
  }

  async deleteAutomationRule(id: string) {
    const response = await this.api.delete(`/automation/rules/${id}`);
    return response.data;
  }

  async getAutomationQueue(params?: { page?: number; limit?: number; status?: string; entity_type?: string }) {
    const response = await this.api.get('/automation/queue', { params });
    return response.data;
  }

  async getAutomationQueueStats() {
    const response = await this.api.get('/automation/queue/stats');
    return response.data;
  }

  async processAutomationQueueItem(id: string) {
    const response = await this.api.post(`/automation/queue/${id}/process`);
    return response.data;
  }

  async getCreditApprovals(params?: { page?: number; limit?: number; status?: string }) {
    const response = await this.api.get('/automation/credit-approvals', { params });
    return response.data;
  }

  async getCreditApprovalStats() {
    const response = await this.api.get('/automation/credit-approvals/stats');
    return response.data;
  }

  async createCreditApprovalRequest(data: any) {
    const response = await this.api.post('/automation/credit-approvals', data);
    return response.data;
  }

  async approveCreditLimit(id: string, data?: { approver_id?: number; notes?: string }) {
    const response = await this.api.post(`/automation/credit-approvals/${id}/approve`, data);
    return response.data;
  }

  async rejectCreditLimit(id: string, data?: { approver_id?: number; notes?: string }) {
    const response = await this.api.post(`/automation/credit-approvals/${id}/reject`, data);
    return response.data;
  }

  async getEntityDocuments(entityType: string, entityId: string, params?: { category?: string }) {
    const response = await this.api.get(`/documents/${entityType}/${entityId}`, { params });
    return response.data;
  }

  async getDocument(id: string) {
    const response = await this.api.get(`/documents/file/${id}`);
    return response.data;
  }

  async uploadDocument(entityType: string, entityId: string, formData: FormData) {
    const response = await this.api.post(`/documents/${entityType}/${entityId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async downloadDocument(id: string) {
    const response = await this.api.get(`/documents/file/${id}/download`, { responseType: 'blob' });
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'document';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async deleteDocument(id: string) {
    const response = await this.api.delete(`/documents/file/${id}`);
    return response.data;
  }

  async getDocumentVersions(id: string) {
    const response = await this.api.get(`/documents/file/${id}/versions`);
    return response.data;
  }

  async uploadDocumentVersion(id: string, formData: FormData) {
    const response = await this.api.post(`/documents/file/${id}/versions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async requestDocumentSignature(id: string, data: { signer_email: string; signer_name: string; version_id?: number }) {
    const response = await this.api.post(`/documents/file/${id}/signatures/request`, data);
    return response.data;
  }

  async submitDocumentSignature(signatureId: string, data: { signature_data: string }) {
    const response = await this.api.post(`/documents/signatures/${signatureId}/sign`, data);
    return response.data;
  }

  async declineDocumentSignature(signatureId: string) {
    const response = await this.api.post(`/documents/signatures/${signatureId}/decline`);
    return response.data;
  }

  async getPendingSignatures(params?: { signer_email?: string }) {
    const response = await this.api.get('/documents/signatures/pending', { params });
    return response.data;
  }

  async getDocumentSignatures(id: string) {
    const response = await this.api.get(`/documents/file/${id}/signatures`);
    return response.data;
  }

  // ============ Countries API ============
  async getCountries(params?: { page?: number; limit?: number; search?: string; region?: string; trade_zone?: string }) {
    const response = await this.api.get('/countries', { params });
    return response.data;
  }

  async getCountry(id: string) {
    const response = await this.api.get(`/countries/${id}`);
    return response.data;
  }

  async createCountry(data: any) {
    const response = await this.api.post('/countries', data);
    return response.data;
  }

  async updateCountry(id: string, data: any) {
    const response = await this.api.put(`/countries/${id}`, data);
    return response.data;
  }

  async deleteCountry(id: string) {
    const response = await this.api.delete(`/countries/${id}`);
    return response.data;
  }

  // ============ Airports API ============
  async getAirports(params?: { page?: number; limit?: number; search?: string; country_code?: string; is_cargo_hub?: boolean }) {
    const response = await this.api.get('/airports', { params });
    return response.data;
  }

  async getAirport(id: string) {
    const response = await this.api.get(`/airports/${id}`);
    return response.data;
  }

  async createAirport(data: any) {
    const response = await this.api.post('/airports', data);
    return response.data;
  }

  async updateAirport(id: string, data: any) {
    const response = await this.api.put(`/airports/${id}`, data);
    return response.data;
  }

  async deleteAirport(id: string) {
    const response = await this.api.delete(`/airports/${id}`);
    return response.data;
  }

  // ============ Shipping Lines API ============
  async getShippingLines(params?: { page?: number; limit?: number; search?: string; alliance?: string }) {
    const response = await this.api.get('/shipping-lines', { params });
    return response.data;
  }

  async getShippingLine(id: string) {
    const response = await this.api.get(`/shipping-lines/${id}`);
    return response.data;
  }

  async createShippingLine(data: any) {
    const response = await this.api.post('/shipping-lines', data);
    return response.data;
  }

  async updateShippingLine(id: string, data: any) {
    const response = await this.api.put(`/shipping-lines/${id}`, data);
    return response.data;
  }

  async deleteShippingLine(id: string) {
    const response = await this.api.delete(`/shipping-lines/${id}`);
    return response.data;
  }

  // ============ Dangerous Goods API ============
  async getDangerousGoods(params?: { page?: number; limit?: number; search?: string }) {
    const response = await this.api.get('/dangerous-goods', { params });
    return response.data;
  }

  async getDangerousGoodsItem(id: string) {
    const response = await this.api.get(`/dangerous-goods/${id}`);
    return response.data;
  }

  async createDangerousGoods(data: any) {
    const response = await this.api.post('/dangerous-goods', data);
    return response.data;
  }

  async updateDangerousGoods(id: string, data: any) {
    const response = await this.api.put(`/dangerous-goods/${id}`, data);
    return response.data;
  }

  async deleteDangerousGoods(id: string) {
    const response = await this.api.delete(`/dangerous-goods/${id}`);
    return response.data;
  }

  // ============ Packaging Types API ============
  async getPackagingTypes(params?: { page?: number; limit?: number; search?: string; category?: string }) {
    const response = await this.api.get('/packaging-types', { params });
    return response.data;
  }

  async getPackagingType(id: string) {
    const response = await this.api.get(`/packaging-types/${id}`);
    return response.data;
  }

  async createPackagingType(data: any) {
    const response = await this.api.post('/packaging-types', data);
    return response.data;
  }

  async updatePackagingType(id: string, data: any) {
    const response = await this.api.put(`/packaging-types/${id}`, data);
    return response.data;
  }

  async deletePackagingType(id: string) {
    const response = await this.api.delete(`/packaging-types/${id}`);
    return response.data;
  }

  // ============ Freight Surcharges API ============
  async getFreightSurcharges(params?: { page?: number; limit?: number; search?: string; is_mandatory?: boolean }) {
    const response = await this.api.get('/freight-surcharges', { params });
    return response.data;
  }

  async getFreightSurcharge(id: string) {
    const response = await this.api.get(`/freight-surcharges/${id}`);
    return response.data;
  }

  async createFreightSurcharge(data: any) {
    const response = await this.api.post('/freight-surcharges', data);
    return response.data;
  }

  async updateFreightSurcharge(id: string, data: any) {
    const response = await this.api.put(`/freight-surcharges/${id}`, data);
    return response.data;
  }

  async deleteFreightSurcharge(id: string) {
    const response = await this.api.delete(`/freight-surcharges/${id}`);
    return response.data;
  }

  // ============ Document Types API ============
  async getDocumentTypes(params?: { page?: number; limit?: number; search?: string; category?: string }) {
    const response = await this.api.get('/document-types', { params });
    return response.data;
  }

  async getDocumentType(id: string) {
    const response = await this.api.get(`/document-types/${id}`);
    return response.data;
  }

  async createDocumentType(data: any) {
    const response = await this.api.post('/document-types', data);
    return response.data;
  }

  async updateDocumentType(id: string, data: any) {
    const response = await this.api.put(`/document-types/${id}`, data);
    return response.data;
  }

  async deleteDocumentType(id: string) {
    const response = await this.api.delete(`/document-types/${id}`);
    return response.data;
  }

  // ============ Units of Measurement API ============
  async getUnits(params?: { page?: number; limit?: number; search?: string; category?: string }) {
    const response = await this.api.get('/units', { params });
    return response.data;
  }

  async getUnit(id: string) {
    const response = await this.api.get(`/units/${id}`);
    return response.data;
  }

  async convertUnit(params: { from_unit: string; to_unit: string; value: number }) {
    const response = await this.api.get('/units/convert', { params });
    return response.data;
  }

  async createUnit(data: any) {
    const response = await this.api.post('/units', data);
    return response.data;
  }

  async updateUnit(id: string, data: any) {
    const response = await this.api.put(`/units/${id}`, data);
    return response.data;
  }

  async deleteUnit(id: string) {
    const response = await this.api.delete(`/units/${id}`);
    return response.data;
  }

  // ============ Free Trade Zones API ============
  async getFreeTradeZones(params?: { page?: number; limit?: number; search?: string; country_code?: string; zone_type?: string }) {
    const response = await this.api.get('/free-trade-zones', { params });
    return response.data;
  }

  async getFreeTradeZone(id: string) {
    const response = await this.api.get(`/free-trade-zones/${id}`);
    return response.data;
  }

  async createFreeTradeZone(data: any) {
    const response = await this.api.post('/free-trade-zones', data);
    return response.data;
  }

  async updateFreeTradeZone(id: string, data: any) {
    const response = await this.api.put(`/free-trade-zones/${id}`, data);
    return response.data;
  }

  async deleteFreeTradeZone(id: string) {
    const response = await this.api.delete(`/free-trade-zones/${id}`);
    return response.data;
  }

  // ============ Commodity Categories API ============
  async getCommodityCategories(params?: { page?: number; limit?: number; search?: string }) {
    const response = await this.api.get('/commodity-categories', { params });
    return response.data;
  }

  async getCommodityCategoriesTree() {
    const response = await this.api.get('/commodity-categories/tree');
    return response.data;
  }

  async getCommodityCategory(id: string) {
    const response = await this.api.get(`/commodity-categories/${id}`);
    return response.data;
  }

  async createCommodityCategory(data: any) {
    const response = await this.api.post('/commodity-categories', data);
    return response.data;
  }

  async updateCommodityCategory(id: string, data: any) {
    const response = await this.api.put(`/commodity-categories/${id}`, data);
    return response.data;
  }

  async deleteCommodityCategory(id: string) {
    const response = await this.api.delete(`/commodity-categories/${id}`);
    return response.data;
  }

  // ============ Payment Terms API ============
  async getPaymentTerms(params?: { page?: number; limit?: number; search?: string; risk_level?: string }) {
    const response = await this.api.get('/payment-terms', { params });
    return response.data;
  }

  async getPaymentTerm(id: string) {
    const response = await this.api.get(`/payment-terms/${id}`);
    return response.data;
  }

  async createPaymentTerm(data: any) {
    const response = await this.api.post('/payment-terms', data);
    return response.data;
  }

  async updatePaymentTerm(id: string, data: any) {
    const response = await this.api.put(`/payment-terms/${id}`, data);
    return response.data;
  }

  async deletePaymentTerm(id: string) {
    const response = await this.api.delete(`/payment-terms/${id}`);
    return response.data;
  }

  // ============ State Machine Workflow Templates API ============
  async getStateMachineTemplates() {
    const response = await this.api.get('/workflows/state-machine/templates');
    return response.data;
  }

  async getStateMachineTemplateById(id: string) {
    const response = await this.api.get(`/workflows/state-machine/templates/${id}`);
    return response.data;
  }

  async getWorkflowInstancesByTemplate(templateId?: string) {
    const params = templateId ? { template_id: templateId } : {};
    const response = await this.api.get('/workflows/instances', { params });
    return response.data;
  }

  async getWorkflowInstanceHistory(instanceId: number) {
    const response = await this.api.get(`/workflows/instances/${instanceId}/history`);
    return response.data;
  }

  async getWorkflowInstanceByEntity(entityType: string, entityId: number) {
    const response = await this.api.get(`/workflows/instances/entity/${entityType}/${entityId}`);
    return response.data;
  }

  async startWorkflowFromTemplate(data: { template_id: string; entity_type: string; entity_id: number; initial_data?: any }) {
    const response = await this.api.post('/workflows/state-machine/start', data);
    return response.data;
  }

  async getWorkflowInstanceState(instanceId: number) {
    const response = await this.api.get(`/workflows/instances/${instanceId}/state`);
    return response.data;
  }

  async transitionWorkflowInstance(instanceId: number, toState: string, data?: { action?: string; comment?: string }) {
    const response = await this.api.post(`/workflows/instances/${instanceId}/transition`, {
      to_state: toState,
      data: data || {}
    });
    return response.data;
  }

  // ============ Workflow Registry API ============
  async getWorkflowRegistry(params?: { entity_type?: string; is_active?: string }) {
    const response = await this.api.get('/workflows/registry', { params });
    return response.data;
  }

  async getWorkflowRegistryStats() {
    const response = await this.api.get('/workflows/registry/stats');
    return response.data;
  }

  async resolveWorkflowRegistry(params: { entity_type: string; service_type?: string; transport_mode?: string; direction?: string }) {
    const response = await this.api.get('/workflows/registry/resolve', { params });
    return response.data;
  }

  async getWorkflowRegistryEntry(id: number) {
    const response = await this.api.get(`/workflows/registry/${id}`);
    return response.data;
  }

  async createWorkflowRegistryEntry(data: {
    entity_type: string; service_type?: string; transport_mode?: string; direction?: string;
    workflow_id: number; is_default?: boolean; priority?: number; auto_bind?: boolean; description?: string;
  }) {
    const response = await this.api.post('/workflows/registry', data);
    return response.data;
  }

  async updateWorkflowRegistryEntry(id: number, data: Record<string, unknown>) {
    const response = await this.api.put(`/workflows/registry/${id}`, data);
    return response.data;
  }

  async deleteWorkflowRegistryEntry(id: number) {
    const response = await this.api.delete(`/workflows/registry/${id}`);
    return response.data;
  }

  async autoBindWorkflow(data: { entity_type: string; entity_id: number; service_type?: string; transport_mode?: string }) {
    const response = await this.api.post('/workflows/registry/auto-bind', data);
    return response.data;
  }

  // ============ Forms Management API ============
  async getFormCategories(params?: { search?: string }) {
    const response = await this.api.get('/forms/categories', { params });
    return response.data;
  }

  async getFormTemplates(params?: { page?: number; limit?: number; search?: string; category_id?: string; entity_type?: string }) {
    const response = await this.api.get('/forms/templates', { params });
    return response.data;
  }

  async getFormTemplateById(id: string) {
    const response = await this.api.get(`/forms/templates/${id}`);
    return response.data;
  }

  async createFormTemplate(data: any) {
    const response = await this.api.post('/forms/templates', data);
    return response.data;
  }

  async updateFormTemplate(id: string, data: any) {
    const response = await this.api.put(`/forms/templates/${id}`, data);
    return response.data;
  }

  async getFormInstances(params?: { page?: number; limit?: number; search?: string; status?: string; template_id?: string }) {
    const response = await this.api.get('/forms/instances', { params });
    return response.data;
  }

  async getFormInstanceById(id: string) {
    const response = await this.api.get(`/forms/instances/${id}`);
    return response.data;
  }

  async createFormInstance(data: any) {
    const response = await this.api.post('/forms/instances', data);
    return response.data;
  }

  async updateFormInstance(id: string, data: any) {
    const response = await this.api.put(`/forms/instances/${id}`, data);
    return response.data;
  }

  async submitFormInstance(id: string) {
    const response = await this.api.post(`/forms/instances/${id}/submit`);
    return response.data;
  }

  async approveFormInstance(id: string, data?: { comment?: string }) {
    const response = await this.api.post(`/forms/instances/${id}/approve`, data);
    return response.data;
  }

  async rejectFormInstance(id: string, data?: { comment?: string; reason?: string }) {
    const response = await this.api.post(`/forms/instances/${id}/reject`, data);
    return response.data;
  }

  async finalizeFormInstance(id: string) {
    const response = await this.api.post(`/forms/instances/${id}/finalize`);
    return response.data;
  }

  async addFormSignature(instanceId: string, data: { signature_data: string; signer_name?: string; signer_role?: string }) {
    const response = await this.api.post(`/forms/instances/${instanceId}/signatures`, data);
    return response.data;
  }

  async getFormStats() {
    const response = await this.api.get('/forms/stats');
    return response.data;
  }

  async getFormsByEntity(entityType: string, entityId: string) {
    const response = await this.api.get(`/forms/by-entity/${entityType}/${entityId}`);
    return response.data;
  }

  async downloadFormPDF(instanceId: string) {
    const response = await this.api.get(`/forms/instances/${instanceId}/pdf`, { responseType: 'blob' });
    return response.data;
  }

  async getFormInstanceHistory(instanceId: string) {
    const response = await this.api.get(`/forms/instances/${instanceId}/history`);
    return response.data;
  }

  async getPayments(params?: { page?: number; limit?: number; status?: string }) {
    const response = await this.api.get('/payments', { params });
    return response.data;
  }
  async getPayment(id: string) {
    const response = await this.api.get(`/payments/${id}`);
    return response.data;
  }
  async createPayment(data: any) {
    const response = await this.api.post('/payments', data);
    return response.data;
  }
  async updatePayment(id: string, data: any) {
    const response = await this.api.put(`/payments/${id}`, data);
    return response.data;
  }
  async deletePayment(id: string) {
    const response = await this.api.delete(`/payments/${id}`);
    return response.data;
  }

  async getCheques(params?: { page?: number; limit?: number; status?: string }) {
    const response = await this.api.get('/cheques', { params });
    return response.data;
  }
  async getCheque(id: string) {
    const response = await this.api.get(`/cheques/${id}`);
    return response.data;
  }
  async createCheque(data: any) {
    const response = await this.api.post('/cheques', data);
    return response.data;
  }
  async updateCheque(id: string, data: any) {
    const response = await this.api.put(`/cheques/${id}`, data);
    return response.data;
  }
  async deleteCheque(id: string) {
    const response = await this.api.delete(`/cheques/${id}`);
    return response.data;
  }

  async getSOAReceivables() {
    const response = await this.api.get('/soa/summary/receivables');
    return response.data;
  }
  async getSOAPayables() {
    const response = await this.api.get('/soa/summary/payables');
    return response.data;
  }
  async getSOAByEntity(entityId: string) {
    const response = await this.api.get(`/soa/entity/${entityId}/statement`);
    return response.data;
  }
  async getSOAAgingReport(params?: { days?: number }) {
    const response = await this.api.get('/soa/aging-report', { params });
    return response.data;
  }

  async getDeliveryOrders(params?: { page?: number; limit?: number; status?: string }) {
    const response = await this.api.get('/delivery-orders', { params });
    return response.data;
  }
  async getDeliveryOrder(id: string) {
    const response = await this.api.get(`/delivery-orders/${id}`);
    return response.data;
  }
  async createDeliveryOrder(data: any) {
    const response = await this.api.post('/delivery-orders', data);
    return response.data;
  }
  async updateDeliveryOrder(id: string, data: any) {
    const response = await this.api.put(`/delivery-orders/${id}`, data);
    return response.data;
  }
  async deleteDeliveryOrder(id: string) {
    const response = await this.api.delete(`/delivery-orders/${id}`);
    return response.data;
  }

  async getVoyages(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/voyages', { params });
    return response.data;
  }
  async createVoyage(data: any) {
    const response = await this.api.post('/voyages', data);
    return response.data;
  }
  async updateVoyage(id: string, data: any) {
    const response = await this.api.put(`/voyages/${id}`, data);
    return response.data;
  }
  async deleteVoyage(id: string) {
    const response = await this.api.delete(`/voyages/${id}`);
    return response.data;
  }

  async getCurrencies(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/currencies', { params });
    return response.data;
  }
  async createCurrency(data: any) {
    const response = await this.api.post('/currencies', data);
    return response.data;
  }
  async updateCurrency(id: string, data: any) {
    const response = await this.api.put(`/currencies/${id}`, data);
    return response.data;
  }
  async deleteCurrency(id: string) {
    const response = await this.api.delete(`/currencies/${id}`);
    return response.data;
  }

  async getHSCodes(params?: { page?: number; limit?: number; search?: string }) {
    if (params?.search) {
      const response = await this.api.get(`/hscodes/search/${encodeURIComponent(params.search)}`);
      return response.data;
    }
    const response = await this.api.get('/hscodes', { params: { page: params?.page, limit: params?.limit } });
    return response.data;
  }
  async createHSCode(data: any) {
    const response = await this.api.post('/hscodes', data);
    return response.data;
  }
  async updateHSCode(id: string, data: any) {
    const response = await this.api.put(`/hscodes/${id}`, data);
    return response.data;
  }
  async deleteHSCode(id: string) {
    const response = await this.api.delete(`/hscodes/${id}`);
    return response.data;
  }

  async getIncoterms() {
    const response = await this.api.get('/incoterms');
    return response.data;
  }
  async createIncoterm(data: any) {
    const response = await this.api.post('/incoterms', data);
    return response.data;
  }
  async updateIncoterm(id: string, data: any) {
    const response = await this.api.put(`/incoterms/${id}`, data);
    return response.data;
  }
  async deleteIncoterm(id: string) {
    const response = await this.api.delete(`/incoterms/${id}`);
    return response.data;
  }

  async getCargoTypes(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/cargo-types', { params });
    return response.data;
  }
  async createCargoType(data: any) {
    const response = await this.api.post('/cargo-types', data);
    return response.data;
  }
  async updateCargoType(id: string, data: any) {
    const response = await this.api.put(`/cargo-types/${id}`, data);
    return response.data;
  }
  async deleteCargoType(id: string) {
    const response = await this.api.delete(`/cargo-types/${id}`);
    return response.data;
  }

  async getPackageTypes(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/package-types', { params });
    return response.data;
  }
  async createPackageType(data: any) {
    const response = await this.api.post('/package-types', data);
    return response.data;
  }
  async updatePackageType(id: string, data: any) {
    const response = await this.api.put(`/package-types/${id}`, data);
    return response.data;
  }
  async deletePackageType(id: string) {
    const response = await this.api.delete(`/package-types/${id}`);
    return response.data;
  }

  async getPaymentTypes(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/payment-types', { params });
    return response.data;
  }
  async createPaymentType(data: any) {
    const response = await this.api.post('/payment-types', data);
    return response.data;
  }
  async updatePaymentType(id: string, data: any) {
    const response = await this.api.put(`/payment-types/${id}`, data);
    return response.data;
  }
  async deletePaymentType(id: string) {
    const response = await this.api.delete(`/payment-types/${id}`);
    return response.data;
  }

  async getVesselTypes(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/vessel-types', { params });
    return response.data;
  }
  async createVesselType(data: any) {
    const response = await this.api.post('/vessel-types', data);
    return response.data;
  }
  async updateVesselType(id: string, data: any) {
    const response = await this.api.put(`/vessel-types/${id}`, data);
    return response.data;
  }
  async deleteVesselType(id: string) {
    const response = await this.api.delete(`/vessel-types/${id}`);
    return response.data;
  }

  async getROE(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/roe', { params });
    return response.data;
  }
  async createROE(data: any) {
    const response = await this.api.post('/roe', data);
    return response.data;
  }
  async updateROE(id: string, data: any) {
    const response = await this.api.put(`/roe/${id}`, data);
    return response.data;
  }
  async deleteROE(id: string) {
    const response = await this.api.delete(`/roe/${id}`);
    return response.data;
  }

  async getLines(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/lines', { params });
    return response.data;
  }
  async createLine(data: any) {
    const response = await this.api.post('/lines', data);
    return response.data;
  }
  async updateLine(id: string, data: any) {
    const response = await this.api.put(`/lines/${id}`, data);
    return response.data;
  }
  async deleteLine(id: string) {
    const response = await this.api.delete(`/lines/${id}`);
    return response.data;
  }

  async getOtmsServices(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/services', { params });
    return response.data;
  }
  async createOtmsService(data: any) {
    const response = await this.api.post('/services', data);
    return response.data;
  }
  async updateOtmsService(id: string, data: any) {
    const response = await this.api.put(`/services/${id}`, data);
    return response.data;
  }
  async deleteOtmsService(id: string) {
    const response = await this.api.delete(`/services/${id}`);
    return response.data;
  }

  async getCommunications(params?: { page?: number; limit?: number; type?: string; status?: string; from_date?: string; to_date?: string }) {
    const response = await this.api.get('/communications/history', { params });
    return response.data;
  }
  async sendEmail(data: any) {
    const response = await this.api.post('/communications/email/send', data);
    return response.data;
  }
  async sendSms(data: any) {
    const response = await this.api.post('/communications/sms/send', data);
    return response.data;
  }
  async getCommunicationTemplates(params?: { page?: number; limit?: number }) {
    const response = await this.api.get('/communications/templates', { params });
    return response.data;
  }

  async getNumberingConfig() {
    const response = await this.api.get('/numbering/config');
    return response.data;
  }

  async updateNumberingConfig(id: number, data: Record<string, any>) {
    const response = await this.api.put(`/numbering/config/${id}`, data);
    return response.data;
  }

  async getNumberingSequences() {
    const response = await this.api.get('/numbering/sequences');
    return response.data;
  }

  async previewNumberFormat(data: { prefix: string; separator: string; year_format: string; sequence_padding: number }) {
    const response = await this.api.post('/numbering/preview', data);
    return response.data;
  }

  async getApprovals(srId: string | number) {
    const response = await this.api.get(`/approvals/${srId}`);
    return response.data;
  }

  async initApprovals(srId: string | number) {
    const response = await this.api.post(`/approvals/${srId}/init`);
    return response.data;
  }

  async submitApprovalDecision(srId: string | number, data: { decision: 'APPROVED' | 'REJECTED'; description?: string; attachments?: any[] }) {
    const response = await this.api.post(`/approvals/${srId}/decide`, data);
    return response.data;
  }

  async getApprovalMessages(srId: string | number) {
    const response = await this.api.get(`/approvals/${srId}/messages`);
    return response.data;
  }

  async sendApprovalMessage(srId: string | number, data: { message: string; attachments?: any[]; approvalId?: number }) {
    const response = await this.api.post(`/approvals/${srId}/messages`, data);
    return response.data;
  }
}

export const otmsApi = new OTMSApiService();
export default otmsApi;
